import React, { useEffect, useState } from 'react'
import { ActivityIndicator, StyleSheet } from 'react-native'

import { FlashList } from '@shopify/flash-list'
import { publishEvent } from '@zet/analytics'
import LazyHOC from '@zet/common/components/lazyHoc'
import {
  BTN,
  Button,
  H6,
  Stack,
  XStack,
  colors as zetColors,
} from '@zet/design-system'
import { Icon } from '@zet/design-system/icons'
import { checkIsMimicMode } from '@zet/utils/userToken'

import { useCoinsTransactionHistory } from '../../api'
import { ZetCoinOutlinedWhite } from '../../assets'
import { i18n } from '../localization'
import { RedeemCoinsBottomSheet } from '../redeem'
import { HeadingComp } from './components/headingComp'
import CoinsLedgerLoadingShimmer from './loadingShimmer'
import { CoinsLedgerSpotlight } from './spotlight'
import { ErrorScreen } from './transactionList/errorScreen'
import { TransactionCard } from './transactionList/transactionCard'

const CoinsLedgerHome = ({ route }) => {
  const {
    data,
    isLoading,
    isError,
    refetch,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    isRefetching,
  } = useCoinsTransactionHistory()

  const [isRedeemBottomSheetOpen, setIsRedeemBottomSheetOpen] = useState(
    Boolean(route?.params?.redeem)
  )
  const coinsHistory = data?.pages.flatMap((page) => page.coinsHistory)

  const openRedeemBottomSheet = () => {
    setIsRedeemBottomSheetOpen(true)

    const CoinsSummary = data?.pages?.[0]?.coinsSummary

    publishEvent('click_action', {
      item_name: 'coins_ledger_use-cta',
      feature: 'coins_ledger',
      total_coins: CoinsSummary?.total,
      used_coins: CoinsSummary?.redeemed,
      balance_coins: CoinsSummary?.balance,
      expired_coins: CoinsSummary?.expired,
    })
  }

  useEffect(() => {
    if (!isLoading && data && !isError) {
      const CoinsSummary = data?.pages?.[0]?.coinsSummary
      publishEvent('screen_view', {
        screen_name: 'coins-ledger',
        state: 'loaded',
        feature: 'coins_ledger',
        total_coins: CoinsSummary?.total,
        used_coins: CoinsSummary?.redeemed,
        balance_coins: CoinsSummary?.balance,
        expired_coins: CoinsSummary?.expired,
      })
    }
    if (!isLoading && isError) {
      publishEvent('screen_view', {
        screen_name: 'coins-ledger',
        state: 'failed',
        feature: 'coins_ledger',
      })
    }
  }, [isLoading])

  const callNextPage = () => {
    if (hasNextPage) {
      fetchNextPage()
    }
  }

  if (isLoading) {
    return <CoinsLedgerLoadingShimmer />
  }

  const ListHeader = () => {
    return (
      <>
        <Stack
          className="bg-tertiary-150"
          style={styles?.ternaryBgColor}
        ></Stack>
        <Stack className="bg-white">
          <CoinsLedgerSpotlight coinsSummary={data?.pages?.[0]?.coinsSummary} />
        </Stack>
        <Stack className="border-grays-40 border-b border-t-4 bg-white">
          <XStack className="items-center p-3">
            <Icon
              className="mx-1"
              type="Transfer2"
              color={zetColors.neutral.black100}
            />
            <H6>{i18n.t('coinsLedger.homeScreen.coinsEarningsHistory')}</H6>
          </XStack>
        </Stack>
      </>
    )
  }

  return (
    <LazyHOC>
      <HeadingComp coinsSummary={data?.pages?.[0]?.coinsSummary} />
      <Stack className="flex-1">
        <FlashList
          data={coinsHistory}
          estimatedItemSize={200}
          ListHeaderComponent={<ListHeader />}
          showsVerticalScrollIndicator={false}
          refreshing={isLoading}
          onRefresh={refetch}
          renderItem={({ item, index }) => <TransactionCard item={item} />}
          ListEmptyComponent={<ErrorScreen refetch={refetch} />}
          ListFooterComponent={
            <Stack>
              {isFetchingNextPage && <ActivityIndicator size={'large'} />}
            </Stack>
          }
          onEndReachedThreshold={0.9}
          onEndReached={callNextPage}
        />
      </Stack>
      {!isError && !checkIsMimicMode() ? (
        <Stack className="p-2">
          <Button size="lg" fullWidth onPress={openRedeemBottomSheet}>
            <XStack className="items-center justify-center gap-2">
              <ZetCoinOutlinedWhite />
              <BTN className="text-white">
                {i18n.t('coinsLedger.cta.useZETCoins')}
              </BTN>
            </XStack>
          </Button>
        </Stack>
      ) : (
        <></>
      )}

      {isRedeemBottomSheetOpen && (
        <RedeemCoinsBottomSheet
          isRedeemBottomSheetOpen={isRedeemBottomSheetOpen}
          setIsRedeemBottomSheetOpen={setIsRedeemBottomSheetOpen}
        />
      )}
    </LazyHOC>
  )
}

const styles = StyleSheet.create({
  ternaryBgColor: { paddingBottom: 130 },
})

export { CoinsLedgerHome }
